import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { dataResetService } from '../../../services/supabaseService';

const DataResetSection = () => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetStatus, setResetStatus] = useState(null);

  const handleResetClick = () => {
    setShowConfirmDialog(true);
    setResetStatus(null);
  };

  const handleConfirmReset = async () => {
    setIsResetting(true);
    setResetStatus(null);

    try {
      const result = await dataResetService?.resetAllData();
      setResetStatus({
        type: 'success',
        message: result?.message || 'Data reset completed successfully'
      });
      setShowConfirmDialog(false);
    } catch (error) {
      setResetStatus({
        type: 'error',
        message: error?.message || 'Failed to reset data. Please try again.'
      });
    } finally {
      setIsResetting(false);
    }
  };

  const handleCancelReset = () => {
    setShowConfirmDialog(false);
    setResetStatus(null);
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="font-heading font-semibold text-xl text-foreground mb-2">
          Data Reset
        </h2>
        <p className="font-body text-muted-foreground text-sm">
          Clear all employee and violation data to start fresh. This action cannot be undone.
        </p>
      </div>

      {/* Status Message */}
      {resetStatus && (
        <div
          className={`
            p-4 rounded-lg flex items-start gap-3
            ${resetStatus?.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}
          `}
        >
          <Icon
            name={resetStatus?.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
            size={20}
            className={resetStatus?.type === 'success' ? 'text-green-600' : 'text-red-600'}
          />
          <div className="flex-1">
            <p className={`font-body text-sm ${resetStatus?.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
              {resetStatus?.message}
            </p>
          </div>
        </div>
      )}

      {/* Warning Card */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-sm text-yellow-900 mb-2">
              Warning: Permanent Action
            </h3>
            <ul className="font-body text-sm text-yellow-800 space-y-1 list-disc list-inside">
              <li>All employee records will be permanently deleted</li>
              <li>All violation records will be permanently deleted</li>
              <li>Dashboard statistics will be reset to zero</li>
              <li>This action cannot be undone</li>
            </ul>
          </div>
        </div>
      </div>

      {/* What Will Be Preserved */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-sm text-blue-900 mb-2">
              What Will Be Preserved
            </h3>
            <ul className="font-body text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Roles and permissions</li>
              <li>Department settings</li>
              <li>Job field configurations</li>
              <li>Violation categories</li>
              <li>Point thresholds</li>
              <li>Compliance rules</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Reset Button */}
      <div className="flex justify-start">
        <Button
          onClick={handleResetClick}
          variant="destructive"
          disabled={isResetting}
          className="flex items-center gap-2"
        >
          <Icon name="Trash2" size={18} />
          {isResetting ? 'Resetting...' : 'Reset All Data'}
        </Button>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg elevation-lg max-w-md w-full p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-full">
                <Icon name="AlertTriangle" size={24} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                  Confirm Data Reset
                </h3>
                <p className="font-body text-sm text-muted-foreground">
                  Are you absolutely sure you want to delete all employee and violation data? This action is permanent and cannot be undone.
                </p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="font-body text-sm text-red-800 font-semibold">
                This will permanently delete:
              </p>
              <ul className="font-body text-sm text-red-700 mt-2 space-y-1 list-disc list-inside">
                <li>All employee records</li>
                <li>All violation records</li>
                <li>All dashboard data</li>
              </ul>
            </div>

            <div className="flex gap-3 justify-end pt-2">
              <Button
                onClick={handleCancelReset}
                variant="outline"
                disabled={isResetting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmReset}
                variant="destructive"
                disabled={isResetting}
                className="flex items-center gap-2"
              >
                {isResetting ? (
                  <>
                    <Icon name="Loader2" size={16} className="animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <Icon name="Trash2" size={16} />
                    Yes, Reset All Data
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataResetSection;